import { useState } from "react";
import { useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { usePostApiChannelConnections } from "@/lib/api/generated/channels/channels";
import { toastMutationError } from "@/lib/utils/formatting";
import { WhatsAppConnectionFields } from "./whatsapp-connection-fields";
import { buildWhatsAppConnectionConfig, createDefaultWhatsAppConnectionFormState } from "./whatsapp-connection-config";
import { SlackConnectionFields } from "./slack-connection-fields";
import { buildSlackConnectionConfig, createDefaultSlackConnectionFormState } from "./slack-connection-config";
import { TeamsConnectionFields } from "./teams-connection-fields";
import { buildTeamsConnectionConfig, createDefaultTeamsConnectionFormState } from "./teams-connection-config";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";

const channelTypes = ["whatsapp", "slack", "msteams", "webchat", "other"] as const;

const CHANNEL_LABELS: Record<string, string> = {
  whatsapp: "WhatsApp",
  slack: "Slack",
  msteams: "Microsoft Teams",
  webchat: "Web Chat",
  other: "Other",
};

const createConnectionSchema = z.object({
  id: z.string().min(1, "ID is required"),
  channelType: z.enum(channelTypes, { required_error: "Channel type is required" }),
  displayName: z.string().min(1, "Display name is required"),
  status: z.enum(["draft", "active", "paused", "error"]).default("draft"),
  config: z.string().default("{}"),
});

type CreateConnectionForm = z.infer<typeof createConnectionSchema>;

const HAS_DEDICATED_FORM = new Set(["whatsapp", "slack", "msteams"]);

export function CreateConnectionPage() {
  const { tenantId } = useTenantContext();
  const navigate = useNavigate();
  const mutation = usePostApiChannelConnections();

  // Per-channel config state
  const [whatsAppConfig, setWhatsAppConfig] = useState(createDefaultWhatsAppConnectionFormState());
  const [slackConfig, setSlackConfig] = useState(createDefaultSlackConnectionFormState());
  const [teamsConfig, setTeamsConfig] = useState(createDefaultTeamsConnectionFormState());

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateConnectionForm>({
    resolver: zodResolver(createConnectionSchema),
    defaultValues: {
      id: `conn_whatsapp_${Date.now()}`,
      channelType: "whatsapp",
      status: "draft",
      config: "{}",
    },
  });

  const channelType = watch("channelType");

  const handleChannelTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value;
    setValue("channelType", newType as CreateConnectionForm["channelType"]);
    setValue("id", `conn_${newType}_${Date.now()}`);
  };

  const buildConfig = (ct: string): Record<string, unknown> => {
    switch (ct) {
      case "whatsapp": return buildWhatsAppConnectionConfig(whatsAppConfig);
      case "slack": return buildSlackConnectionConfig(slackConfig);
      case "msteams": return buildTeamsConnectionConfig(teamsConfig);
      default: return JSON.parse(watch("config") || "{}");
    }
  };

  const onSubmit = async (data: CreateConnectionForm) => {
    try {
      const config = buildConfig(data.channelType);
      await mutation.mutateAsync({
        data: {
          id: data.id,
          channelType: data.channelType,
          displayName: data.displayName,
          status: data.status,
          config,
        },
      });
      toast.success(`Connection "${data.displayName}" created`);
      navigate("/workspace/channels");
    } catch (err) {
      toast.error(toastMutationError(err, "Failed to create channel connection"));
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      <h1 className="text-lg font-semibold mb-6" style={{ color: "var(--text-strong)" }}>
        New Channel Connection
      </h1>
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <FormField label="ID" error={errors.id?.message}>
              <input {...register("id")} className="form-input font-mono" />
            </FormField>

            <FormField label="Channel Type" error={errors.channelType?.message}>
              <select value={channelType} onChange={handleChannelTypeChange} className="form-input">
                {channelTypes.map((ct) => (
                  <option key={ct} value={ct}>{CHANNEL_LABELS[ct] ?? ct}</option>
                ))}
              </select>
            </FormField>

            <FormField label="Display Name" error={errors.displayName?.message}>
              <input {...register("displayName")} placeholder={CHANNEL_LABELS[channelType] ?? "Connection"} className="form-input" />
            </FormField>

            <FormField label="Status">
              <select {...register("status")} className="form-input">
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
              </select>
            </FormField>

            {/* Per-channel config forms */}
            {channelType === "whatsapp" && (
              <WhatsAppConnectionFields value={whatsAppConfig} onChange={setWhatsAppConfig} />
            )}
            {channelType === "slack" && (
              <SlackConnectionFields value={slackConfig} onChange={setSlackConfig} />
            )}
            {channelType === "msteams" && (
              <TeamsConnectionFields value={teamsConfig} onChange={setTeamsConfig} />
            )}
            {/* Generic fallback for channel types without dedicated forms */}
            {!HAS_DEDICATED_FORM.has(channelType) && (
              <FormField label="Config (JSON)">
                <textarea
                  {...register("config")}
                  rows={6}
                  className="form-input font-mono text-xs"
                  placeholder="{}"
                />
                <p className="text-[11px]" style={{ color: "var(--muted-foreground)" }}>
                  Channel-specific configuration as JSON.
                </p>
              </FormField>
            )}

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Creating..." : "Create Connection"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => navigate("/workspace/channels")}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function FormField({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>{label}</label>
      {children}
      {error && <span className="text-xs" style={{ color: "var(--danger)" }}>{error}</span>}
    </div>
  );
}
